import React from "react";
import { LibraryItem } from "@/contexts/UniversalLibraryContext/types";
import { Card } from "@/components/ui/card";
import { OptimizedImage } from "@/components/ui/OptimizedImage";
import { getContentTypeConfig } from "./utils/contentTypeConfig";
import AddToProjectButton from "@/components/private-image-generator/AddToProjectButton";
import { PublicImageAddToProjectButton } from "@/components/image-generator";
import ResearchAddToProjectButton from "@/components/private-research-generator/AddToProjectButton";
import { PublicResearchAddToProjectButton } from "@/components/research-generator";
import AddToLibraryButton from "./AddToLibraryButton";

interface Props {
  item: LibraryItem;
}

const UniversalStorageGhost: React.FC<Props> = ({ item }) => {
  const contentConfig = getContentTypeConfig(item.contentType);
  const IconComponent = contentConfig.icon;

  return (
    <div className="opacity-60 rotate-6 scale-105 transform-gpu">
      <Card className="relative overflow-hidden aspect-square border-2 border-dashed border-purple-400 shadow-2xl bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-900 dark:to-gray-800/80 backdrop-blur-sm w-[280px]">
        {/* Bottom Left: Content Type Badge with Label (only for image types) */}
        {(item.contentType === 'image' || item.contentType === 'private_image') && (
          <div className="absolute bottom-2 left-2 z-20">
            <div className={`
              relative flex items-center gap-2 px-2 py-1 rounded-md
              backdrop-blur-md border border-white/20
              shadow-lg
              before:absolute before:inset-0 before:rounded-md
              before:bg-gradient-to-br before:from-white/15 before:to-transparent
              after:absolute after:inset-0 after:rounded-md
              after:bg-gradient-to-t after:from-black/5 after:to-transparent
              transform-gpu
            `}
            style={{
              background: `${
                contentConfig.variant === 'blue' ? 'rgba(59, 130, 246, 0.3)' :
                contentConfig.variant === 'green' ? 'rgba(34, 197, 94, 0.3)' :
                contentConfig.variant === 'purple' ? 'rgba(147, 51, 234, 0.3)' :
                contentConfig.variant === 'orange' ? 'rgba(249, 115, 22, 0.3)' :
                'rgba(107, 114, 128, 0.3)'
              }`
            }}>
              <IconComponent size={14} className="text-white/90 drop-shadow-sm flex-shrink-0" />
              <span className="text-white/90 text-xs font-medium drop-shadow-sm">
                {contentConfig.label}
              </span>
            </div>
          </div>
        )}

        {/* Bottom Right: Project and Storage buttons side by side */}
        <div className="absolute bottom-2 right-2 z-30 flex flex-row gap-2 pointer-events-none">
          {/* Add to Project button (top) - for images and research */}
          {(item.contentType === 'private_image' || item.contentType === 'image' || item.contentType === 'private_research' || item.contentType === 'research') && (
            item.contentType === 'private_image' ? (
              <AddToProjectButton
                itemId={item.originalItem?.id || item.id}
                contentType="private_image" 
                variant="storage"
                forceWhiteText={true}
                dropdownLabel="Add to Project"
              />
            ) : item.contentType === 'private_research' ? (
              <ResearchAddToProjectButton
                itemId={item.originalItem?.id || item.id}
                variant="auto"
                forceWhiteText={true}
                dropdownLabel="Add to Project"
              />
            ) : item.contentType === 'research' ? (
              <PublicResearchAddToProjectButton
                itemId={item.originalItem?.id || item.id}
                variant="auto"
                forceWhiteText={true}
                dropdownLabel="Add to Project"
              />
            ) : (
              <PublicImageAddToProjectButton
                itemId={item.originalItem?.id || item.id}
                variant="storage"
                forceWhiteText={true}
                dropdownLabel="Add to Project"
              />
            )
          )}
          
          {/* Add to Folder button */}
          <AddToLibraryButton
            itemId={item.contentType === 'workbook' ? item.id : (item.originalItem?.id || item.id)}
            contentType={item.contentType as 'image' | 'research' | 'workbook' | 'app_idea' | 'private_image' | 'private_research'}
            dropdownLabel="Add to Folder"
            variant="storage"
          />
        </div>

        {/* Main Content Area */}
        <div className="relative w-full h-full">
          {item.imageUrl ? (
            item.contentType === 'workbook' ? (
              // Workbook with Cover Image - Hybrid layout
              <div className="w-full h-full relative overflow-hidden rounded-lg">
                {/* Image fills remaining space above title area */}
                <div className="absolute inset-0 top-0 bottom-10 overflow-hidden">
                  <OptimizedImage
                    src={item.imageUrl}
                    alt={item.title}
                    className="object-cover w-full h-full"
                    style={{ objectPosition: 'center' }}
                  />
                </div>
                
                {/* Colored Background Section for title area */}
                <div className={`absolute inset-0 top-auto bottom-0 h-10 ${
                  contentConfig.variant === 'purple' ? 'bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/40 dark:to-purple-800/40' :
                  'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-900/40 dark:to-gray-800/40'
                }`} />
                

              </div>
            ) : (
              // Regular Image Content - Full bleed for pure images
              <div className="w-full h-full relative overflow-hidden rounded-lg">
                <OptimizedImage
                  src={item.imageUrl}
                  alt={item.title}
                  className="object-cover w-full h-full"
                  aspectRatio="1/1"
                />

              </div>
            )
          ) : (
            // Non-image Content - Beautiful gradient backgrounds
            <div className="w-full h-full relative">
              {/* Background gradient based on content type */}
              <div className={`absolute inset-0 opacity-20 bg-gradient-to-br ${
                contentConfig.variant === 'blue' ? 'from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40' :
                contentConfig.variant === 'green' ? 'from-green-100 to-green-200 dark:from-green-900/40 dark:to-green-800/40' :
                contentConfig.variant === 'purple' ? 'from-purple-100 to-purple-200 dark:from-purple-900/40 dark:to-purple-800/40' :
                contentConfig.variant === 'orange' ? 'from-orange-100 to-orange-200 dark:from-orange-900/40 dark:to-orange-800/40' :
                'from-gray-100 to-gray-200 dark:from-gray-900/40 dark:to-gray-800/40'
              }`} />
              
              {/* Large content type icon in center */}
              <div className="absolute inset-0 flex items-center justify-center z-0">
                <IconComponent size={64} className={`opacity-30 ${
                  contentConfig.variant === 'blue' ? 'text-blue-500' :
                  contentConfig.variant === 'green' ? 'text-green-500' :
                  contentConfig.variant === 'purple' ? 'text-purple-500' :
                  contentConfig.variant === 'orange' ? 'text-orange-500' :
                  'text-gray-500'
                }`} />
              </div>
              
              {/* Title at bottom */}
              <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm leading-tight truncate">
                  {item.title}
                </h3>
              </div>
            </div>
          )}
        </div>

        {/* Dragging overlay effect */}
        <div className="absolute inset-0 bg-gradient-to-t from-purple-500/20 to-purple-500/10 pointer-events-none" />
      </Card>
    </div>
  );
};

export default UniversalStorageGhost; 